/**
 * App Component - Sólido Business Law
 * Diseño minimalista inspirado en Viralistic
 */

import React, { useState } from "react";
import { ThemeProvider } from "@mui/material/styles";
import {
  CssBaseline,
  Box,
  Container,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import {
  Email,
  Phone,
  LocationOn,
  LinkedIn,
  Twitter,
} from "@mui/icons-material";
import solidoTheme from "./styles/themes/solidoTheme";
import Navigation from "./components/layout/Navigation";
import Hero from "./components/sections/Hero";
import GlobalBackground from "./components/layout/GlobalBackground";
import ScrollInteraction from "./components/ui/ScrollInteraction";
import ScrollText from "./components/ui/ScrollText";
import GlassCard from "./components/ui/GlassCard";
import ParticleBackground from "./components/ui/ParticleBackground";
import InnovationTabs from "./components/ui/InnovationTabs";
import ContactForm from "./components/ui/ContactForm";
import ClientsSection from "./components/sections/ClientsSection";

const App: React.FC = () => {
  const [openModal, setOpenModal] = useState(false);
  const [openModalMinero, setOpenModalMinero] = useState(false);
  const [openModalPublico, setOpenModalPublico] = useState(false);
  
  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };
  
  const handleOpenModalMinero = () => {
    setOpenModalMinero(true);
  };

  const handleCloseModalMinero = () => {
    setOpenModalMinero(false);
  };

  const handleOpenModalPublico = () => {
    setOpenModalPublico(true);
  };

  const handleCloseModalPublico = () => {
    setOpenModalPublico(false);
  };

  const [openModalInnovacion, setOpenModalInnovacion] = useState(false);

  const handleOpenModalInnovacion = () => {
    setOpenModalInnovacion(true);
  };

  const handleCloseModalInnovacion = () => {
    setOpenModalInnovacion(false);
  };

  const [openModalLaboral, setOpenModalLaboral] = useState(false);

  const handleOpenModalLaboral = () => {
    setOpenModalLaboral(true);
  };

  const handleCloseModalLaboral = () => {
    setOpenModalLaboral(false);
  };

  const [selectedMember, setSelectedMember] = useState<any>(null);
  const handleCloseMemberModal = () => setSelectedMember(null);

  return (
    <ThemeProvider theme={solidoTheme}>
      <CssBaseline />

      {/* Global Background */}
      <GlobalBackground />

      {/* Navigation */}
      <Navigation
        items={[
          { label: "HOME", href: "#home" },
          { label: "QUIÉNES SOMOS", href: "#quienes-somos" },
          { label: "ÁREAS", href: "#areas" },
          { label: "INNOVACIÓN", href: "#innovacion" },
          { label: "TALENTO", href: "#talento" },
          { label: "CLIENTES", href: "#clientes" },
          { label: "CONTACTO", href: "#contacto" },
        ]}
      />

      {/* Hero Section */}
      <Box id="home">
        <Hero
          title="El derecho, elevado a su máxima precisión."
          subtitle="Sólido Business Law. Derecho que lidera. Tecnología que acelera."
          description="En Sólido transformamos la práctica jurídica en una herramienta estratégica para los negocios. No somos una firma tradicional: somos un equipo de expertos en derecho de la empresa y laboral, que combina técnica jurídica rigurosa con tecnología aplicada—inteligencia artificial, automatización y analítica de datos—para anticipar riesgos, fortalecer estructuras corporativas y responder con precisión a la velocidad que exigen las compañías actuales.

Nuestro modelo integra conocimiento jurídico profundo con soluciones inteligentes que estandarizan calidad, optimizan tiempos y elevan la seguridad legal en cada operación. El derecho sigue siendo el centro; la tecnología, el impulso que lo hace avanzar."
          ctaText="Conoce nuestro enfoque"
          ctaHref="#quienes-somos"
        />
      </Box>

      {/* Quiénes Somos */}
      <Box
        id="quienes-somos"
        sx={{
          py: { xs: 8, md: 12 },
          backgroundColor: "#1F2330",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Imagen de fondo fragmentada con transición suave */}
        <Box
          sx={{
            position: "absolute",
            top: "-10%",
            right: "-5%",
            width: "60%",
            height: "120%",
            backgroundImage: "url(/bosque-fondo.jpg)",
            backgroundSize: "cover",
            backgroundPosition: "center right",
            backgroundRepeat: "no-repeat",
            opacity: 0.15,
            zIndex: 0,
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                "linear-gradient(90deg, #1F2330 0%, rgba(31, 35, 48, 0.95) 25%, rgba(31, 35, 48, 0.7) 40%, transparent 60%)",
            },
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: "-20%",
              left: 0,
              right: 0,
              height: "20%",
              background:
                "linear-gradient(180deg, transparent 0%, #1F2330 100%)",
            },
          }}
        />
        {/* Línea divisoria con verde */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: "10%",
            right: "10%",
            height: "1px",
            background:
              "linear-gradient(90deg, transparent, rgba(0, 225, 112, 0.4), transparent)",
            zIndex: 1,
          }}
        />

        <Container
          maxWidth="lg"
          sx={{ position: "relative", zIndex: 2, px: { xs: 4, md: 6 } }}
        >
          <Box sx={{ maxWidth: "1000px", mx: "auto" }}>
            <ScrollInteraction animationType="slide" delay={0}>
              <Typography
                variant="overline"
                sx={{
                  fontSize: "0.6875rem",
                  fontWeight: 400,
                  letterSpacing: "0.08em",
                  color: "#B8C5B8",
                  mb: 3,
                  textTransform: "uppercase",
                  opacity: 0.9,
                  display: "block",
                }}
              >
                Quiénes Somos
              </Typography>
              <ScrollText
                variant="h2"
                animationType="word"
                stagger={0.05}
                sx={{
                  fontSize: { xs: "2rem", md: "3rem", lg: "3.5rem" },
                  fontWeight: 700,
                  mb: 4,
                  color: "#FFFFFF",
                  lineHeight: 1.1,
                  letterSpacing: "-0.01em",
                  textAlign: "left",
                }}
              >
                El derecho, diseñado para un nuevo modelo de negocios.
              </ScrollText>
            </ScrollInteraction>

            <ScrollInteraction animationType="fade" delay={0.2}>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: "1rem", md: "1.125rem" },
                  fontWeight: 400,
                  color: "#B8C5B8",
                  mb: 6,
                  lineHeight: 1.7,
                  textAlign: "left",
                  maxWidth: "900px",
                }}
              >
                En Sólido creemos que la práctica legal moderna no consiste sólo en interpretar normas, sino en construir soluciones prácticas que acompañen la velocidad y la complejidad del entorno empresarial actual.
                <br /><br />
                Somos una firma impulsada por el rigor técnico, la claridad estratégica y el uso inteligente de la tecnología. Combinamos experiencia en derecho corporativo, contratación (pública y privada), derecho administrativo, laboral y regulación del sector minero-energético. Nuestra asesoría se apoya en metodologías avanzadas, analítica jurídica e inteligencia artificial aplicada para anticipar riesgos, optimizar procesos y elevar el estándar de seguridad jurídica de nuestros clientes.
              </Typography>

            </ScrollInteraction>

            <ScrollInteraction animationType="fade" delay={0.3}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                  gap: 3,
                  mt: 8,
                  mb: 4,
                }}
              >
                {[
                  { 
                    name: "Enrique José Arboleda Perdomo", 
                    img: "/Gemini_Generated_Image_12l4o212l4o212l4.png",
                    bio: `Enrique José Arboleda Perdomo, Doctor en Jurisprudencia del Colegio Mayor de Nuestra Señora del Rosario (1976), Magister (DEA) y candidato a Doctor de Tercer Ciclo en Ciencia Política, (Institut d’Études Politiques de Paris 1979).\n\nExconsejero de Estado de la Sala de Consulta y Servicio Civil, (2004 – 2012) Perteneció a la Comisión redactora del actual Código de Procedimiento Administrativo y de lo Contencioso Administrativo, así como la del decreto ley 01 de 1984.\n\nAutor de libros y artículos siendo el mas conocido el de Comentarios al Nuevo Código de Procedimiento Administrativo y de lo Contencioso Administrativo que lleva tres ediciones. Posee amplia experiencia en las áreas del derecho administrativo y constitucional, en especial en contratos estatales, servicios públicos, reglamentación y regulación, redacción de normas y contratos. Es miembro de la lista A de árbitros del Centros de Conciliación y Arbitraje de la Cámara de Comercio de Bogotá. Profesor titular de la Universidad de Rosario en las cátedras de derecho público. Miembro correspondiente de la Academia Colombiana de Jurisprudencia y de varia asociaciones profesionales.`
                  },
                  { name: "Salome Marrugo", img: "/Gemini_Generated_Image_tcvuvetcvuvetcvu.png", position: "top center" },
                  { 
                    name: "Luis Gabriel Mendoza Macanaz", 
                    role: "Socio",
                    email: "lmendoza@solidolegal.com",
                    phone: "+57 311 591 6643",
                    img: "/Gemini_Generated_Image_wrya4awrya4awrya.png", 
                    position: "top center",
                    bio: `Luis Gabriel cuenta con más de 20 años de experiencia en derecho laboral, seguridad social y compliance, asesorando estratégicamente a compañías de diversos sectores económicos. Su trayectoria profesional combina el ejercicio legal en firmas de alto prestigio con una visión corporativa práctica, adquirida al liderar áreas de relaciones laborales en empresas multinacionales de consumo masivo.\n\nAntes de incorporarse como Socio a Sólido Legal, se desempeñó como Gerente de Relaciones Laborales en Nestlé de Colombia, donde manejó la estrategia de relaciones individuales y colectivas, así como investigaciones de compliance y códigos de conducta. Su experiencia incluye la gestión como HR Business Partner para mercados en Colombia y Ecuador, liderando temas de talento humano en múltiples fábricas y gestionando equipos de gran escala.\n\nSu enfoque integral le permite no solo atender litigios y consultas complejas, sino también estructurar estrategias preventivas, procesos de due diligence y negociaciones colectivas que se alinean con los objetivos de negocio y la sostenibilidad de sus clientes.\n\nEDUCACIÓN\n• Especialista en Derecho Laboral, Pontificia Universidad Javeriana (2013).\n• Especialista en Derecho Administrativo, Universidad del Rosario (Becado, 2009).\n• Abogado, Universidad del Rosario (Eximido de preparatorios por promedio destacado, 2007).\n• Cursos de Inglés, Language Studies International, Londres, Reino Unido.\n\nEXPERIENCIA LABORAL\n• Socio, Sólido Legal (Actualidad).\n• Gerente de Relaciones Laborales, Nestlé de Colombia.\n• Consultor Independiente, Asesoría en derecho laboral individual, colectivo y seguridad social.\n• Abogado Asociado, José Lloreda Camacho & Co. Atendiendo clientes como Roche, Coca-Cola y Boehringer Ingelheim.\n• Abogado Litigante, Herrera Laboralistas.\n• Judicatura, Corte Constitucional (Despacho Dr. Marco Gerardo Monroy).\n\nÁREAS DE PRÁCTICA\n• Derecho Laboral Individual y Colectivo.\n• Seguridad Social y Pensiones.\n• Compliance Corporativo e Investigaciones Internas.\n• Gestión de Recursos Humanos y Relaciones Industriales.`
                  },
                  { 
                    name: "Mauricio Pardo Rojas", 
                    role: "Asociado Senior de Proyectos",
                    email: "mpardo@solidolegal.com",
                    img: "/Mauricio Pardo Rojas - Solido.jpeg",
                    bio: `Mauricio es un consultor y gestor experto con más de 20 años de experiencia liderando la modernización de áreas de Recursos Humanos. Su perfil combina el conocimiento técnico de la administración de nómina y compensación con un dominio avanzado de herramientas tecnológicas y analítica de datos.\n\nEn su rol como Asociado Senior de Proyectos en Sólido Legal, lidera la implementación de soluciones de LegalTech y automatización para garantizar la eficiencia operativa y el blindaje jurídico de los clientes. Su trayectoria incluye la coordinación de auditorías laborales y el desarrollo de productos low-code en firmas legales como Scola Abogados, donde logró incrementos significativos en eficiencia comercial y operativa.\n\nCuenta con experiencia multinacional y multisectorial, habiendo liderado departamentos de nómina de gran volumen (más de 3.000 empleados) para organizaciones como Hoteles Estelar y Restcafé (Frayco), así como la implementación de sistemas de gestión en Oriente Medio (Bahrein) para Sepam Middle East. Es experto en el manejo de requerimientos ante la UGPP y en el uso de inteligencia artificial aplicada a la gestión del talento.\n\nEDUCACIÓN Y CERTIFICACIONES\n• Profesional en Administración de Empresas, Universidad EAN (2018).\n• Inteligencia Artificial y Data Science, Certificaciones en MinTIC Talento Tech y LinkedIn Learning (2024-2025).\n• Analítica de Datos y Business Intelligence (Power BI), Platzi y Udemy (2022).\n• Gestión de Proyectos, Universidad EAN (2025).\n• Técnico en Administración de Recursos Humanos (2004).\n\nEXPERIENCIA LABORAL\n• Asociado Senior de Proyectos, Sólido Legal (Actualidad).\n• Consultor en Transformación Laboral, Grupo Diverservicios. Automatización de nómina y compensación estratégica.\n• Coordinador de Auditorías y Soluciones Digitales, Scola Abogados. Desarrollo de herramientas low-code para auditoría y cumplimiento legal.\n• Jefe Corporativo de Nómina, Hoteles Estelar. Gestión de nómina para más de 3.000 colaboradores y manejo de outsourcing.\n• Project Lead (HR Systems), Sepam Middle East (Bahrein). Implementación de sistemas de nómina y biométricos en 5 países.\n• Director de Nómina, Restcafé – Frayco. Atención de auditorías UGPP y gestión de nómina masiva.\n\nÁREAS DE PRÁCTICA\n• Auditoría de Nómina y Gestión ante la UGPP.\n• LegalTech: Automatización de Procesos Legales y de RR.HH. (Power Platform, Python).\n• Analítica de Datos para Compensación y Beneficios (Power BI).\n• Implementación de Sistemas de Información de RR.HH.\n• Consultoría en Transformación Digital Laboral.`
                  },
                  { 
                    name: "Katherine Guerrero Buitrago", 
                    role: "Socia",
                    email: "katherine.guerrero@solidolegal.com",
                    img: "/Katherine Guerrero.png",
                    bio: `Katherine cuenta con más de doce años de experiencia liderando estrategias de litigio y consultoría empresarial en el ámbito laboral y de la seguridad social. Como Socia Fundadora de ALYRA Abogados, dirige la gestión ejecutiva de la firma, asegurando la excelencia técnica en las distintas áreas de especialización y liderando la representación ante clientes clave.\n\nSu trayectoria profesional incluye un sólido paso por firmas de alto reconocimiento como Álvarez, Liévano & Laserna y Herrera Laboralistas, donde perfeccionó su práctica en la representación judicial de compañías, manejo de trámites administrativos ante entes de control y acompañamiento en mesas de negociación sindical.\n\nComplementa su práctica privada con una visión del sector público, actuando actualmente como Asesora Externa de Colpensiones, donde maneja la defensa judicial de la entidad. Su perfil destaca por una profunda formación académica, ostentando una Maestría en Derecho Laboral, lo que le permite abordar conflictos jurídicos con un alto nivel de tecnicismo y rigor estratégico.\n\nEDUCACIÓN\n• Maestría en Derecho Laboral y Seguridad Social, Pontificia Universidad Javeriana (2021).\n• Especialización en Derecho Laboral y Seguridad Social, Pontificia Universidad Javeriana (2017).\n• Diplomado en Gerencia Jurídica Pública, Escuela Superior de Administración Pública (2024).\n• Diplomado en Talento Humano, Pontificia Universidad Javeriana (2023).\n• Abogada, Universidad La Gran Colombia (2013).\n\nEXPERIENCIA LABORAL\n• Socia Fundadora, ALYRA Abogados S.A.S. (Actualidad).\n• Asesora Externa, Administradora Colombiana de Pensiones (Colpensiones) (Actualidad).\n• Abogada Laboralista, Herrera Laboralistas S.A.S.\n• Docente de Derecho Laboral, Unihorizonte.\n• Abogada Laboralista, Álvarez, Liévano & Laserna S.A.S.`
                  },
                  { 
                    name: "Juliana Perales", 
                    role: "Asociada",
                    email: "juliana.perales@solidolegal.com",
                    img: "/Maria Juliana Perales.jpg",
                    bio: `Juliana es una abogada especialista en Derecho de Sociedades con un enfoque estratégico en los sectores minero-energético e hidrocarburos. Su práctica profesional se centra en la estructuración legal de proyectos de energía, combinando la precisión regulatoria con una visión corporativa integral.\n\nCuenta con experiencia en la elaboración y negociación de contratos de la industria, así como en la gestión de trámites complejos ante entidades regulatorias y autoridades ambientales. Asimismo, ha participado activamente en transacciones de fusiones y adquisiciones (M&A) para clientes de diversas industrias, asegurando el cumplimiento normativo en operaciones de alto impacto.\n\nAntes de incorporarse a la Firma, consolidó su trayectoria trabajando en firmas legales líderes en Colombia, lo que le ha permitido desarrollar un criterio jurídico riguroso y adaptado a las necesidades del mercado actual.\n\nEDUCACIÓN\n• Especialista en Derecho de Sociedades, Pontificia Universidad Javeriana (2024).\n• Abogada, Universidad del Rosario (2022).\n\nEXPERIENCIA RELEVANTE\n• Asociada – Oil & Gas / Regulatorio, Arboleda Abogados (Actualidad). Asesoría en proyectos energéticos y cumplimiento regulatorio.\n• Abogada, Firmas Líderes en Colombia. Experiencia previa en derecho corporativo y gestión contractual.\n\nÁREAS DE PRÁCTICA\n• Derecho de Hidrocarburos y Minería (Oil & Gas).\n• Derecho Corporativo y M&A.\n• Regulación Energética y Ambiental.\n• Contratación Empresarial y Trámites Administrativos.`
                  },
                  { 
                    name: "Nicolás Arboleda", 
                    role: "Socio",
                    email: "nicolas.arboleda@solidolegal.com",
                    phone: "+57 300 532 5753",
                    img: "/team/Nicolas-Arboleda-Marquez.png",
                    bio: `Nicolás es un abogado experto en derecho de la empresa con más de 16 años de experiencia asesorando a compañías, inversionistas y sponsors en el desarrollo de proyectos y transacciones complejas en América Latina, África y la región EMEA.\n\nSu práctica se distingue por integrar una visión estratégica de negocios —fortalecida por su MBA en el Reino Unido— con una ejecución jurídica técnica. Se especializa en la estructuración y negociación de fusiones y adquisiciones (M&A), financiación de proyectos y estrategias regulatorias en sectores clave como energía, infraestructura, minería y tecnología.\n\nAntes de su rol actual, Nicolás consolidó una trayectoria internacional y corporativa única: fue Socio de la firma Cuatrecasas en Colombia, Vicepresidente Legal de proyectos de gran escala (Soto Norte y La Luna) y Legal Counsel de Desarrollo de Negocios para QKR Corporation en Londres y Qatar. Esta experiencia dual (firma y gerencia legal interna) le permite traducir complejidades regulatorias en soluciones viables para la inversión.\n\nEDUCACIÓN\n• MBA – Master of Business Administration, University of Dundee, Reino Unido (2021).\n• Especialización en Derecho Minero y Petrolero, Universidad del Rosario (2014).\n• Maestría en Derecho Administrativo, Universidad del Rosario (2010).\n• Abogado, Universidad del Rosario (2009).\n\nTRAYECTORIA Y RECONOCIMIENTOS\n• Socio, Sólido Legal (Actualidad).\n• Socio, Cuatrecasas (Colombia).\n• Vicepresidente Legal, Proyecto Soto Norte y Proyecto La Luna.\n• Legal Counsel – Business Development, QKR Corporation (Londres/Qatar).\n• Reconocimientos: Destacado por Chambers & Partners como Partner Up & Coming (2024-2025) y por The Legal 500 como Rising Star (2021-2022).\n• Academia: Profesor de posgrado en la Universidad de los Andes.\n\nÁREAS DE PRÁCTICA\n• Desarrollo de Proyectos y Financiación.\n• Derecho Corporativo y M&A.\n• Estrategia Regulatoria y Compliance.\n• Recursos Naturales (Minería, Oil & Gas) e Infraestructura.`
                  },
                  {
                    name: "Mónica Rave Ospina",
                    role: "Asociada",
                    email: "mrave@solidolegal.com",
                    img: "/Monica.png",
                    bio: `Mónica es una abogada con una sólida trayectoria en consultoría empresarial, litigio laboral y gestión de recursos humanos. Su experiencia combina el ejercicio en firmas legales de alto nivel con roles de dirección en el sector público, lo que le otorga una visión integral para la resolución de conflictos organizacionales.\n\nActualmente se desempeña como Asociada en Sólido Legal, donde aporta su experiencia estratégica en la defensa judicial y asesoría corporativa. Previamente, ejerció como Abogada Laboralista en la firma Álvarez, Liévano & Laserna, destacándose como abogada In-House para Enel Colombia y la Empresa de Telecomunicaciones de Bogotá (ETB). En esta posición, lideró temas complejos de seguridad social, incluyendo la gestión de cuotas partes, compartibilidad pensional, liquidaciones y la defensa ante trámites administrativos y tutelas.\n\nSu perfil se distingue por su capacidad administrativa y de liderazgo, habiendo ejercido como Subcontralora (E) en la Contraloría Municipal de Soacha, donde dirigió el área de Talento Humano y coordinó procesos disciplinarios y de bienestar laboral. Mónica complementa su práctica con una formación académica robusta, siendo especialista tanto en Derecho Laboral como en Derecho Contractual.\n\nEDUCACIÓN\n• Candidata a Magíster en Derecho Laboral y de la Seguridad Social, Universidad del Rosario (2023).\n• Especialista en Derecho Laboral y de la Seguridad Social, Universidad del Rosario (2022).\n• Especialista en Derecho Contractual, Universidad del Rosario (2017).\n• Abogada, Universidad La Gran Colombia (2015).\n\nEXPERIENCIA LABORAL\n• Asociada, Sólido Legal (Actualidad).\n• Abogada Laboralista y In-House, Álvarez, Liévano & Laserna S.A.S. Asesoría directa a Enel y ETB en pasivos pensionales y litigios.\n• Subcontralora (E) y Directora de Talento Humano, Contraloría Municipal de Soacha. Dirección de procesos de selección, SG-SST y régimen disciplinario.\n• Asistente Judicial, Oficina Jurídica Juan Rico. Manejo de procesos civiles, de familia y administrativos.\n\nÁREAS DE PRÁCTICA\n• Derecho Laboral Individual y Seguridad Social.\n• Gestión de Pasivos Pensionales y Cuotas Partes.\n• Derecho Contractual y Administrativo.\n• Litigio y Defensa Judicial.\n• Gestión Estratégica de Recursos Humanos y SG-SST.`
                  },
                  {
                    name: "Jhonny Castro Suárez",
                    role: "Asociado",
                    email: "jcastro@solidolegal.com",
                    img: "/Jonny Castro - solido.png",
                    bio: `Jhonny es un abogado con una trayectoria consolidada en la asesoría jurídica integral a empresas y entidades educativas. Su práctica profesional combina una sólida experiencia en derecho privado y comercial —específicamente en contratación y protección de activos intangibles— con una especialización en derecho administrativo y defensa constitucional.\n\nComo Asociado en Sólido Legal, lidera la estructuración y negociación de contratos mercantiles, acuerdos de confidencialidad y licenciamientos, además de gestionar la protección de marcas y derechos de autor para clientes corporativos.\n\nAdicionalmente, Jhonny cuenta con una destacada experiencia en el sector educativo, actuando como asesor jurídico de instituciones de renombre como la Universidad Santo Tomás y diversos colegios privados. En este ámbito, se especializa en la defensa técnica ante acciones de tutela, el manejo de procesos disciplinarios y la recuperación estratégica de cartera. Su visión preventiva y litigiosa le permite mitigar riesgos legales tanto en la jurisdicción ordinaria como en la contencioso-administrativa.\n\nEDUCACIÓN\n• Especialista en Derecho Administrativo, Universidad del Rosario.\n• Abogado, Universidad del Rosario (2008).\n\nEXPERIENCIA RELEVANTE\n• Asociado, Sólido Legal (Actualidad). Asesoría en derecho comercial, propiedad intelectual y litigio estratégico.\n• Asesor Jurídico y de Cobranzas, Universidad Santo Tomás y Colegio Franciscano Virrey Solís. Defensa en tutelas y recuperación de activos.\n• Asesor Jurídico, Colegio Santo Tomás de Aquino y Colegio Jordán de Sajonia. Gestión de cumplimiento normativo escolar y contratación privada.\n• Sustanciador, Procuraduría General de la Nación. Proyección de fallos y vigilancia administrativa.\n\nÁREAS DE PRÁCTICA\n• Derecho Comercial y Contratación Mercantil.\n• Propiedad Intelectual (Marcas y Derechos de Autor).\n• Derecho Educativo y Defensa Constitucional (Tutelas).\n• Gestión de Cartera y Litigio Ejecutivo.\n• Derecho Administrativo.\n\n"Mi enfoque integra la prevención del riesgo legal en la contratación comercial con una defensa constitucional robusta, protegiendo los intereses patrimoniales y reputacionales de mis clientes."`
                  },
                ].map((member, index) => (
                  <Box 
                    key={index} 
                    sx={{ textAlign: 'center', cursor: 'pointer' }}
                    onClick={() => setSelectedMember(member)}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        aspectRatio: '1/1',
                        backgroundImage: `url("${member.img}")`,
                        backgroundSize: 'cover',
                        backgroundPosition: (member as any).position || 'center',
                        mb: 2,
                        filter: 'grayscale(100%)',
                        transition: 'all 0.5s ease',
                        border: '1px solid rgba(184, 197, 184, 0.2)',
                        '&:hover': {
                          filter: 'grayscale(0%)',
                          borderColor: '#00e170',
                          boxShadow: '0 0 15px rgba(0, 225, 112, 0.3)',
                        }
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#D4E0D4',
                        fontSize: '0.8125rem',
                        fontWeight: 400,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}
                    >
                      {member.name}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </ScrollInteraction>

            {/* Team Member Modal */}
            <Dialog
              open={!!selectedMember}
              onClose={handleCloseMemberModal}
              maxWidth="md"
              fullWidth
              PaperProps={{
                style: {
                  backgroundColor: 'rgba(36, 44, 39, 0.98)',
                  backdropFilter: 'blur(12px)',
                  border: '1px solid rgba(0, 225, 112, 0.2)',
                  color: '#ffffff',
                  borderRadius: '0px',
                },
              }}
            >
              {selectedMember && (
                <Box sx={{ p: { xs: 3, md: 5 } }}>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 4,
                    mb: 2
                  }}>
                    {/* Image Section */}
                    <Box sx={{ width: { xs: '100%', md: '300px' }, flexShrink: 0 }}>
                      <Box
                        sx={{
                          width: '100%',
                          aspectRatio: '1/1',
                          backgroundImage: `url("${selectedMember.img}")`,
                          backgroundSize: 'cover',
                          backgroundPosition: selectedMember.position || 'center',
                          border: '1px solid rgba(184, 197, 184, 0.2)',
                          filter: 'grayscale(0%)',
                          boxShadow: '0 0 20px rgba(0, 225, 112, 0.1)',
                        }}
                      />
                    </Box>

                    {/* Content Section */}
                    <Box sx={{ flex: 1 }}>
                      <Typography 
                        variant="h4" 
                        sx={{ 
                          color: '#FFFFFF', 
                          fontWeight: 600, 
                          mb: 1,
                          fontSize: { xs: '1.5rem', md: '2rem' }
                        }}
                      >
                        {selectedMember.name}
                      </Typography>

                      <Box sx={{ mb: 3 }}>
                        {selectedMember.role && (
                           <Typography variant="h6" sx={{ color: '#00e170', mb: 1.5, fontWeight: 500, fontSize: '1.1rem' }}>
                             {selectedMember.role}
                           </Typography>
                        )}
                        {selectedMember.email && (
                          <Typography variant="body2" sx={{ color: '#B8C5B8', display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5, fontSize: '0.9rem' }}>
                            <Email sx={{ color: '#00e170', fontSize: '1.1rem' }}/> 
                            <a href={`mailto:${selectedMember.email}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                              {selectedMember.email}
                            </a>
                          </Typography>
                        )}
                         {selectedMember.phone && (
                          <Typography variant="body2" sx={{ color: '#B8C5B8', display: 'flex', alignItems: 'center', gap: 1.5, fontSize: '0.9rem' }}>
                            <Phone sx={{ color: '#00e170', fontSize: '1.1rem' }}/>
                            <a href={`tel:${selectedMember.phone.replace(/\s/g, '')}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                               {selectedMember.phone}
                            </a>
                          </Typography>
                        )}
                      </Box>
                      
                      <Box sx={{ width: '40px', height: '2px', bgcolor: 'rgba(255, 255, 255, 0.1)', mb: 3 }} />

                      <Typography 
                        variant="body1" 
                        sx={{ 
                          color: '#B8C5B8',
                          lineHeight: 1.8,
                          whiteSpace: 'pre-line' 
                        }}
                      >
                        {selectedMember.bio || "Perfil en construcción."}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </Dialog>


          </Box>
        </Container>
      </Box>

      {/* Separador 1 - Imagen con frase llamativa */}
      <Box
        sx={{
          position: "relative",
          minHeight: "70vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        {/* Imagen de fondo con parallax effect simulado */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: "url(/bosque-separador.jpg)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundAttachment: "fixed", // Parallax effect
            zIndex: 1,
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(31, 35, 48, 0.6)", // Oscurecer ligeramente
              zIndex: 2,
            },
          }}
        />

        {/* Contenido */}
        <Container
          maxWidth="md"
          sx={{ position: "relative", zIndex: 3, px: { xs: 4, md: 6 } }}
        >
          <Box sx={{ textAlign: "center" }}>
            <ScrollInteraction animationType="scale" delay={0.2}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "2.5rem", md: "4rem", lg: "5rem" },
                  fontWeight: 700,
                  color: "#FFFFFF",
                  lineHeight: 1.2,
                  letterSpacing: "-0.02em",
                  mb: 4,
                  textShadow: "0 4px 30px rgba(0, 0, 0, 0.5)",
                }}
              >
                No solo resolvemos asuntos legales.
              </Typography>
            </ScrollInteraction>
            
            <ScrollInteraction animationType="fade" delay={0.6}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "2.5rem", md: "4rem", lg: "5rem" },
                  fontWeight: 400,
                  color: "#B8C5B8",
                  lineHeight: 1.2,
                  letterSpacing: "-0.02em",
                  fontStyle: "italic",
                  textShadow: "0 4px 30px rgba(0, 0, 0, 0.5)",
                }}
              >
                Diseñamos cómo se estructura el futuro jurídico de las organizaciones.
              </Typography>
            </ScrollInteraction>
          </Box>
        </Container>
      </Box>

      {/* Áreas de Práctica */}
      <Box
        id="areas"
        sx={{
          py: { xs: 8, md: 12 },
          backgroundColor: "#242C27",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Imagen de fondo fragmentada - esquina inferior derecha con transición suave */}
        <Box
          sx={{
            position: "absolute",
            top: "-10%",
            bottom: "-10%",
            right: "-5%",
            width: "55%",
            height: "120%",
            backgroundImage: "url(/bosque-fondo.jpg)",
            backgroundSize: "cover",
            backgroundPosition: "center right",
            backgroundRepeat: "no-repeat",
            opacity: 0.1,
            zIndex: 0,
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                "linear-gradient(135deg, #242C27 0%, rgba(36, 44, 39, 0.95) 30%, rgba(36, 44, 39, 0.7) 50%, transparent 70%)",
            },
            "&::after": {
              content: '""',
              position: "absolute",
              top: "-20%",
              left: 0,
              right: 0,
              height: "20%",
              background:
                "linear-gradient(180deg, #242C27 0%, transparent 100%)",
            },
          }}
        />
        {/* Línea divisoria con verde */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: "10%",
            right: "10%",
            height: "1px",
            background:
              "linear-gradient(90deg, transparent, rgba(0, 225, 112, 0.4), transparent)",
            zIndex: 1,
          }}
        />
        <Container maxWidth="lg" sx={{ px: { xs: 4, md: 6 } }}>
          <Box sx={{ maxWidth: "1200px", mx: "auto", mb: 6 }}>
            <Typography
              variant="overline"
              sx={{
                fontSize: "0.6875rem",
                fontWeight: 400,
                letterSpacing: "0.08em",
                color: "#B8C5B8",
                mb: 2,
                textTransform: "uppercase",
                opacity: 0.9,
                display: "block",
              }}
            >
              Especialización
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "2rem", md: "3rem", lg: "3.5rem" },
                fontWeight: 700,
                mb: 3,
                color: "#B8C5B8",
                lineHeight: 1.1,
                letterSpacing: "-0.01em",
                textAlign: "left",
              }}
            >
              La estrategia legal que acelera los negocios.
            </Typography>

            <Typography
              variant="h4"
              sx={{
                fontSize: { xs: "1rem", md: "1.25rem", lg: "1.375rem" },
                fontWeight: 400,
                color: "#D4E0D4",
                fontStyle: "italic",
                textAlign: "left",
                maxWidth: "700px",
                lineHeight: 1.6,
                mb: 0,
              }}
            >
              "Lo mejor del pensamiento jurídico. Con la velocidad del
              pensamiento tecnológico."
            </Typography>
          </Box>

          {/* Áreas Grid */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
              gap: { xs: 4, md: 5 },
              maxWidth: "1200px",
              mx: "auto",
            }}
          >
            {[
              {
                title: "Derecho de Empresa & de los Negocios",
                desc: "Incluye Derecho Comercial, Derecho de Sociedades, Derecho Transaccional, y Secretaría General.",
                features: ["Comercial & Societario", "Transaccional", "Secretaría General"],
                ctaText: "Conoce más",
                // ctaHref: "#contacto", // Removed to use onClick
                onCtaClick: handleOpenModal,
              },
              {
                title: "Asesoría en Regulación del Sector Minero Energético",
                desc: "El conocimiento en el sector se basa en una fuerte experiencia regulatoria y la asistencia a clientes en todas las fases de sus proyectos (desde la entrada al país hasta el desarrollo de los proyectos).",
                features: ["Regulación", "Licenciamiento", "Fases del Proyecto"],
                ctaText: "Conoce más",
                // ctaHref: "#contacto",
                onCtaClick: handleOpenModalMinero,
              },
              {
                title: "Derecho Público & Relaciones con el Estado",
                desc: "Incluye Asesoría en Gestión Contractual, Accountability Gerencial, Derecho laboral, seguridad social y migratorio, y Litigios.",
                features: ["Gestión Contractual", "Accountability", "Litigios"],
                ctaText: "Conoce más",
                // ctaHref: "#contacto",
                onCtaClick: handleOpenModalPublico,
              },
              {
                title: "Innovación & Cumplimiento",
                desc: "El enfoque de Sólido utiliza la tecnología para acelerar lo repetitivo, estandarizando lo que antes consumía semanas. Diseño e implementación de programas de cumplimiento y gestión de riesgos.",
                features: ["Legal Tech", "Compliance", "Estandarización"],
                ctaText: "Conoce más",
                // ctaHref: "#contacto",
                onCtaClick: handleOpenModalInnovacion,
              },
              {
                title: "Derecho laboral, Litigios, seguridad social y migratorio",
                desc: "Sólido ofrece asesoría integral en derecho laboral y de la seguridad social, abarcando la gestión de relaciones individuales (contratación, remuneración y desvinculación) y colectivas, y realizando Due Diligence para mitigar contingencias y preservar la sostenibilidad empresarial.",
                features: ["Relaciones Individuales", "Derecho Colectivo", "Due Diligence Laboral"],
                ctaText: "Conoce más",
                // ctaHref: "#contacto",
                onCtaClick: handleOpenModalLaboral,
              },

            ].map((area, index) => (
              <Box 
                key={index} 
                sx={{ 
                  height: '100%',
                  width: '100%',
                  ...(index === 4 && {
                    gridColumn: { md: '1 / -1' },
                    maxWidth: { md: '600px' },
                    justifySelf: { md: 'center' },
                  })
                }}
              >
                <GlassCard
                  title={area.title}
                  description={area.desc}
                  features={area.features}
                  ctaText={area.ctaText}
                  delay={index * 0.1}
                  onCtaClick={area.onCtaClick}
                />
              </Box>
            ))}
          </Box>
          
          {/* Modal Derecho de Empresa */}
          <Dialog
            open={openModal}
            onClose={handleCloseModal}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            PaperProps={{
              style: {
                backgroundColor: 'rgba(36, 44, 39, 0.98)', // Slightly more opaque
                backdropFilter: 'blur(12px)',
                border: '1px solid rgba(0, 225, 112, 0.2)',
                color: '#ffffff',
                borderRadius: '0px',
                maxWidth: '1000px', // Wider for horizontal layout
                width: '100%',
              },
            }}
          >
            <DialogTitle id="alert-dialog-title" sx={{ pt: 4, pb: 2, px: { xs: 3, md: 5 } }}>
              <Typography variant="overline" sx={{ color: '#B8C5B8', fontSize: '0.75rem', letterSpacing: '0.1em' }}>
                Área de Práctica
              </Typography>
              <Typography variant="h4" sx={{ color: '#FFFFFF', textTransform: 'uppercase', letterSpacing: '0.02em', fontWeight: 600, fontSize: { xs: '1.5rem', md: '2rem' } }}>
                Derecho de Empresa & de los Negocios
              </Typography>
            </DialogTitle>
            
            <DialogContent sx={{ px: { xs: 3, md: 5 }, pb: 4 }}>
              <Box 
                sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, 
                  gap: { xs: 4, md: 4 },
                  mt: 2 
                }}
              >
                {/* Servicio 1: Derecho de Sociedades */}
                <Box>
                  <Box sx={{ borderBottom: '1px solid rgba(184, 197, 184, 0.3)', mb: 3, pb: 1 }}>
                     <Typography variant="h6" sx={{ color: '#B8C5B8', textTransform: 'uppercase', fontSize: '1rem', letterSpacing: '0.05em', fontWeight: 500 }}>
                      Derecho de Sociedades
                    </Typography>
                  </Box>
                  <Box sx={{ color: '#D4E0D4', fontFamily: 'inherit', lineHeight: 1.8 }}>
                    <Typography paragraph sx={{ fontSize: '0.95rem' }}>
                      Asesoría integral en materia de derecho comercial para los negocios.
                    </Typography>
                    <Typography paragraph sx={{ fontSize: '0.95rem' }}>
                      Asesoría en contratos privados, como la revisión o la elaboración de contratos y la asistencia en la negociación, celebración, ejecución y liquidación de todo tipo de contratos y sus garantías.
                    </Typography>
                    <Typography sx={{ fontSize: '0.95rem' }}>
                      Asesoría en relación con la suscripción de contratos de colaboración empresarial, tales como el Joint Venture o Consorcio y la Cuentas en Participación.
                    </Typography>
                  </Box>
                </Box>

                {/* Servicio 2: Derecho Transaccional */}
                <Box>
                  <Box sx={{ borderBottom: '1px solid rgba(184, 197, 184, 0.3)', mb: 3, pb: 1 }}>
                    <Typography variant="h6" sx={{ color: '#B8C5B8', textTransform: 'uppercase', fontSize: '1rem', letterSpacing: '0.05em', fontWeight: 500 }}>
                      Derecho Transaccional
                    </Typography>
                  </Box>
                  <Box component="ul" sx={{ pl: 2, m: 0, color: '#D4E0D4', fontFamily: 'inherit', lineHeight: 1.8 }}>
                    {[
                      "Debida diligencia legal.",
                      "Asesoría legal integral sobre las distintas estructuras para adquirir una compañía, así como negociación e implementación del contrato respectivo.",
                      "Compraventa de acciones (SPA).",
                      "Fusión con la empresa a adquirir o necesidad de otra modalidad de organización corporativa.",
                      "Compraventa de activos (APA).",
                      "Financiaciones de diferentes categorías, incluyendo con banca privada o inter-compañía."
                    ].map((item, i) => (
                      <Typography component="li" key={i} sx={{ mb: 1.5, fontSize: '0.95rem' }}>{item}</Typography>
                    ))}
                  </Box>
                </Box>

                {/* Servicio 3: Secretaría General */}
                <Box>
                  <Box sx={{ borderBottom: '1px solid rgba(184, 197, 184, 0.3)', mb: 3, pb: 1 }}>
                    <Typography variant="h6" sx={{ color: '#B8C5B8', textTransform: 'uppercase', fontSize: '1rem', letterSpacing: '0.05em', fontWeight: 500 }}>
                      Secretaría General
                    </Typography>
                  </Box>
                  <Box component="ul" sx={{ pl: 2, m: 0, color: '#D4E0D4', fontFamily: 'inherit', lineHeight: 1.8 }}>
                    {[
                      "Representación legal para temas contractuales y comerciales.",
                      "Mantenimiento legal de la sociedad, cumplimiento de las obligaciones legales y/o estatutarias recurrentes.",
                      "Revisión y regularización de los libros societarios obligatorios.",
                      "Asistencia a Juntas Directivas o Comités de Dirección para absolver dudas legales presencialmente.",
                      "Elaboración de las Actas donde se plasmen los acuerdos adoptados y aseguramiento de las formalidades a cumplirse en atención a los actos acordados.",
                      "Seguimiento a la inscripción oportuna de los actos sujetos a registro ante las diferentes entidades públicas o privadas."
                    ].map((item, i) => (
                      <Typography component="li" key={i} sx={{ mb: 1.5, fontSize: '0.95rem' }}>{item}</Typography>
                    ))}
                  </Box>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
              <Button 
                onClick={handleCloseModal} 
                autoFocus
                variant="outlined"
                sx={{
                  color: '#B8C5B8',
                  borderColor: 'rgba(184, 197, 184, 0.4)',
                  '&:hover': {
                    borderColor: '#FFFFFF',
                    color: '#FFFFFF',
                    backgroundColor: 'rgba(255,255,255,0.05)'
                  }
                }}
              >
                Cerrar
              </Button>
            </DialogActions>
          </Dialog>

          {/* Modal Regulación Minero Energética */}
          <Dialog
            open={openModalMinero}
            onClose={handleCloseModalMinero}
            aria-labelledby="alert-dialog-title-minero"
            aria-describedby="alert-dialog-description-minero"
            PaperProps={{
              style: {
                backgroundColor: 'rgba(36, 44, 39, 0.98)',
                backdropFilter: 'blur(12px)',
                border: '1px solid rgba(184, 197, 184, 0.2)',
                color: '#ffffff',
                borderRadius: '0px',
                maxWidth: '1000px',
                width: '100%',
              },
            }}
          >
            <DialogTitle id="alert-dialog-title-minero" sx={{ pt: 4, pb: 2, px: { xs: 3, md: 5 } }}>
              <Typography variant="overline" sx={{ color: '#B8C5B8', fontSize: '0.75rem', letterSpacing: '0.1em' }}>
                Área de Práctica
              </Typography>
              <Typography variant="h4" sx={{ color: '#FFFFFF', textTransform: 'uppercase', letterSpacing: '0.02em', fontWeight: 600, fontSize: { xs: '1.5rem', md: '2rem' } }}>
                Regulación del Sector Minero Energético
              </Typography>
            </DialogTitle>
            
            <DialogContent sx={{ px: { xs: 3, md: 5 }, pb: 4 }}>
              <Box 
                sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, 
                  gap: { xs: 4, md: 6 },
                  mt: 2 
                }}
              >
                {/* Servicio 1 */}
                <Box>
                  <Box sx={{ borderBottom: '1px solid rgba(184, 197, 184, 0.3)', mb: 3, pb: 1 }}>
                     <Typography variant="h6" sx={{ color: '#B8C5B8', textTransform: 'uppercase', fontSize: '1rem', letterSpacing: '0.05em', fontWeight: 500 }}>
                      Asesoría y Gestión Regulatoria
                    </Typography>
                  </Box>
                  <Box sx={{ color: '#D4E0D4', fontFamily: 'inherit', lineHeight: 1.8 }}>
                    <Typography paragraph sx={{ fontSize: '0.95rem' }}>
                      Nuestro conocimiento en el sector minero-energético se basa en la fuerte experiencia en temas regulatorios y la asistencia a nuestros clientes en todas las fases de sus proyectos; desde su entrada al país como inversionistas nuevos hasta su desarrollo de los mismos.
                    </Typography>
                    <Typography paragraph sx={{ fontSize: '0.95rem' }}>
                      Al conocer muy bien la alta regulación del sector y su coyuntura actual, quienes tienen activos operativos también cuentan con asesoría de nosotros en relación con temas contractuales y de negocios específicos para este sector tan especializado.
                    </Typography>
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, mt: 2, mb: 1, color: '#FFFFFF' }}>
                      Asesoría legal para los sectores:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, m: 0 }}>
                      {["Minería", "Energía eléctrica", "Hidrocarburos (Upstream, Midstream, Downstream)"].map((item, i) => (
                        <Typography component="li" key={i} sx={{ mb: 0.5, fontSize: '0.95rem' }}>{item}</Typography>
                      ))}
                    </Box>
                  </Box>
                </Box>

                {/* Servicio 2 */}
                <Box>
                  <Box sx={{ borderBottom: '1px solid rgba(184, 197, 184, 0.3)', mb: 3, pb: 1 }}>
                    <Typography variant="h6" sx={{ color: '#B8C5B8', textTransform: 'uppercase', fontSize: '1rem', letterSpacing: '0.05em', fontWeight: 500 }}>
                      Gestión de Proyectos & Cumplimiento
                    </Typography>
                  </Box>
                  <Box component="ul" sx={{ pl: 2, m: 0, color: '#D4E0D4', fontFamily: 'inherit', lineHeight: 1.8 }}>
                    {[
                      "Seguimiento a cumplimiento de obligaciones regulatorias y ambientales.",
                      "Diseño de estrategia legal para la consecución de permisos o licencias requeridas.",
                      "Elaboración, negociación y cierre de contratos de operación, EPC/EPCM, y paquetes de garantía.",
                      "Cesiones de derechos mineros o petroleros.",
                      "Contratos de compraventa de energía (PPA) y suministro (ToP / TaP).",
                      "Realización de auditorías legales regulatorias para analizar cumplimiento vigente.",
                      "Asesoría en estrategias legales para manejo de conflictos con comunidades y stakeholders."
                    ].map((item, i) => (
                      <Typography component="li" key={i} sx={{ mb: 1.5, fontSize: '0.95rem' }}>{item}</Typography>
                    ))}
                  </Box>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
              <Button 
                onClick={handleCloseModalMinero} 
                autoFocus
                variant="outlined"
                sx={{
                  color: '#B8C5B8',
                  borderColor: 'rgba(184, 197, 184, 0.4)',
                  '&:hover': {
                    borderColor: '#FFFFFF',
                    color: '#FFFFFF',
                    backgroundColor: 'rgba(255,255,255,0.05)'
                  }
                }}
              >
                Cerrar
              </Button>
            </DialogActions>
          </Dialog>

          {/* Modal Derecho Público */}
          <Dialog
            open={openModalPublico}
            onClose={handleCloseModalPublico}
            aria-labelledby="alert-dialog-title-publico"
            aria-describedby="alert-dialog-description-publico"
            PaperProps={{
              style: {
                backgroundColor: 'rgba(36, 44, 39, 0.98)',
                backdropFilter: 'blur(12px)',
                border: '1px solid rgba(184, 197, 184, 0.2)',
                color: '#ffffff',
                borderRadius: '0px',
                maxWidth: '1200px', // Wider due to content density
                width: '100%',
              },
            }}
          >
            <DialogTitle id="alert-dialog-title-publico" sx={{ pt: 4, pb: 2, px: { xs: 3, md: 5 } }}>
              <Typography variant="overline" sx={{ color: '#B8C5B8', fontSize: '0.75rem', letterSpacing: '0.1em' }}>
                Área de Práctica
              </Typography>
              <Typography variant="h4" sx={{ color: '#FFFFFF', textTransform: 'uppercase', letterSpacing: '0.02em', fontWeight: 600, fontSize: { xs: '1.5rem', md: '2rem' } }}>
                Derecho Público & Relaciones con el Estado
              </Typography>
            </DialogTitle>
            
            <DialogContent sx={{ px: { xs: 3, md: 5 }, pb: 4 }}>
              <Box 
                sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, 
                  gap: { xs: 4, md: 4 },
                  mt: 2 
                }}
              >
                {/* Columna 1: Perspectiva y Oportunidad */}
                <Box>
                  <Box sx={{ borderBottom: '1px solid rgba(184, 197, 184, 0.3)', mb: 3, pb: 1 }}>
                     <Typography variant="h6" sx={{ color: '#B8C5B8', textTransform: 'uppercase', fontSize: '1rem', letterSpacing: '0.05em', fontWeight: 500 }}>
                      Relaciones con el Estado
                    </Typography>
                  </Box>
                  <Box sx={{ color: '#D4E0D4', fontFamily: 'inherit', lineHeight: 1.8 }}>
                    <Typography paragraph sx={{ fontSize: '0.95rem' }}>
                      Desde una perspectiva empresarial, el Estado puede ser analizado como una oportunidad de negocios y como un organizador de la actividad privada.
                    </Typography>
                    <Typography paragraph sx={{ fontSize: '0.95rem' }}>
                      En el primer caso, son múltiples los negocios que se originan directamente por el Estado y más concretamente por la administración, como las concesiones de infraestructura, de minas, de petróleos, algunos servicios públicos, y múltiples contratos públicos en los que el Estado actúa bien como consumidor bien como proveedor de bienes y servicios.
                    </Typography>
                    <Typography sx={{ fontSize: '0.95rem' }}>
                      Negociación y preparación de todo tipo de contratos o acuerdos donde la contraparte sea el estado. Asesoría en materia de servicios públicos domiciliarios.
                    </Typography>
                  </Box>
                </Box>

                {/* Columna 2: Regulación e Intervención */}
                <Box>
                  <Box sx={{ borderBottom: '1px solid rgba(184, 197, 184, 0.3)', mb: 3, pb: 1 }}>
                    <Typography variant="h6" sx={{ color: '#B8C5B8', textTransform: 'uppercase', fontSize: '1rem', letterSpacing: '0.05em', fontWeight: 500 }}>
                      Intervención Económica
                    </Typography>
                  </Box>
                  <Box sx={{ color: '#D4E0D4', fontFamily: 'inherit', lineHeight: 1.8 }}>
                    <Typography paragraph sx={{ fontSize: '0.95rem' }}>
                      Encontramos la regulación e intervención económica que modulan determinadas actividades como la financiera, la de servicios públicos que se prestan bajo un régimen de libre empresa. la inspección, vigilancia y control de múltiples actividades económicas, por lo general en cabeza de las superintendencias.
                    </Typography>
                    <Typography paragraph sx={{ fontSize: '0.95rem' }}>
                      En estos últimos casos, las decisiones administrativas son individuales, sobre las corporaciones, en particular, que pueden dar lugar a procesos de toma de posesión, de cancelación de permisos y autorizaciones, sanciones de carácter pecuniario, etc.
                    </Typography>
                    <Typography sx={{ fontSize: '0.95rem' }}>
                      El ejercicio de estas funciones tiene claros límites en la Constitución y la ley, indispensable contar con profesionales expertos en regulación y funciones de superintendencias.
                    </Typography>
                  </Box>
                </Box>
                
                {/* Columna 3: Litigio y Defensa */}
                 <Box>
                  <Box sx={{ borderBottom: '1px solid rgba(184, 197, 184, 0.3)', mb: 3, pb: 1 }}>
                    <Typography variant="h6" sx={{ color: '#B8C5B8', textTransform: 'uppercase', fontSize: '1rem', letterSpacing: '0.05em', fontWeight: 500 }}>
                      Litigio & Defensa
                    </Typography>
                  </Box>
                  <Box component="ul" sx={{ pl: 2, m: 0, color: '#D4E0D4', fontFamily: 'inherit', lineHeight: 1.8 }}>
                    {[
                      "Asesoría en estrategia legal para llevar procesos judiciales o arbitrales en contra de la administración pública.",
                      "Preparación de escritos de defensa, recursos de reposición o apelación.",
                      "Redacción de tutelas, acciones de grupo o populares para la protección de derechos frente a la administración pública.",
                      "Revisión de contratos estatales, negociación y asesoría en la ejecución (concesión, obra pública, convenios interadministrativos).",
                      "Representación ante entidades públicas en defensa de intereses corporativos."
                    ].map((item, i) => (
                      <Typography component="li" key={i} sx={{ mb: 1.5, fontSize: '0.95rem' }}>{item}</Typography>
                    ))}
                  </Box>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
              <Button 
                onClick={handleCloseModalPublico} 
                autoFocus
                variant="outlined"
                sx={{
                  color: '#B8C5B8',
                  borderColor: 'rgba(184, 197, 184, 0.4)',
                  '&:hover': {
                    borderColor: '#FFFFFF',
                    color: '#FFFFFF',
                    backgroundColor: 'rgba(255,255,255,0.05)'
                  }
                }}
              >
                Cerrar
              </Button>
            </DialogActions>
          </Dialog>

          {/* Modal Innovación & Cumplimiento */}
          <Dialog
            open={openModalInnovacion}
            onClose={handleCloseModalInnovacion}
            aria-labelledby="alert-dialog-title-innovacion"
            aria-describedby="alert-dialog-description-innovacion"
            PaperProps={{
              style: {
                backgroundColor: 'rgba(36, 44, 39, 0.98)',
                backdropFilter: 'blur(12px)',
                border: '1px solid rgba(184, 197, 184, 0.2)',
                color: '#ffffff',
                borderRadius: '0px',
                maxWidth: '1000px',
                width: '100%',
              },
            }}
          >
            <DialogTitle id="alert-dialog-title-innovacion" sx={{ pt: 4, pb: 2, px: { xs: 3, md: 5 } }}>
              <Typography variant="overline" sx={{ color: '#B8C5B8', fontSize: '0.75rem', letterSpacing: '0.1em' }}>
                Área de Práctica
              </Typography>
              <Typography variant="h4" sx={{ color: '#FFFFFF', textTransform: 'uppercase', letterSpacing: '0.02em', fontWeight: 600, fontSize: { xs: '1.5rem', md: '2rem' } }}>
                Innovación & Cumplimiento
              </Typography>
            </DialogTitle>
            
            <DialogContent sx={{ px: { xs: 3, md: 5 }, pb: 4 }}>
              <Box 
                sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, 
                  gap: { xs: 4, md: 6 },
                  mt: 2 
                }}
              >
                {/* Columna 1: Asesoría en Gestión Contractual */}
                <Box>
                  <Box sx={{ borderBottom: '1px solid rgba(184, 197, 184, 0.3)', mb: 3, pb: 1 }}>
                     <Typography variant="h6" sx={{ color: '#B8C5B8', textTransform: 'uppercase', fontSize: '1rem', letterSpacing: '0.05em', fontWeight: 500 }}>
                      Asesoría en Gestión Contractual
                    </Typography>
                  </Box>
                  <Box sx={{ color: '#D4E0D4', fontFamily: 'inherit', lineHeight: 1.8 }}>
                    <Typography paragraph sx={{ fontSize: '0.95rem' }}>
                      Brindar asesoraría en la gestión contractual de las entidades sujetas al régimen de contratación pública y de empresas altamente reguladas mediante disposiciones normativas aplicables a eventuales contingencias jurídicas.
                    </Typography>
                    <Typography paragraph sx={{ fontSize: '0.95rem' }}>
                      Realizar un diagnóstico legal respecto al cumplimiento con la legislación.
                    </Typography>
                    <Typography sx={{ fontSize: '0.95rem' }}>
                      Consultoría regulatoria para la elaboración e implementación de soluciones de “compliance” con enfoque de cumplimiento y/o de reporting, mediante una herramienta de compilación normativa, estructurada para gestionar y controlar el cumplimiento de las obligaciones legales de las Compañías.
                    </Typography>
                  </Box>
                </Box>

                {/* Columna 2: Accountability Gerencial */}
                <Box>
                  <Box sx={{ borderBottom: '1px solid rgba(184, 197, 184, 0.3)', mb: 3, pb: 1 }}>
                    <Typography variant="h6" sx={{ color: '#B8C5B8', textTransform: 'uppercase', fontSize: '1rem', letterSpacing: '0.05em', fontWeight: 500 }}>
                      Accountability Gerencial
                    </Typography>
                  </Box>
                  <Box component="ul" sx={{ pl: 2, m: 0, color: '#D4E0D4', fontFamily: 'inherit', lineHeight: 1.8 }}>
                    {[
                      "Asesoría legal sobre normativa Anti Lavado de Activos, Ética Empresarial y Cumplimiento Anticorrupción.",
                      "Auditorías legales o de debida diligencia que permitan brindar asesoría legal para el desarrollo de políticas, lineamientos, mejores prácticas y estrategias empresariales para la prevención, detección y mitigación de los riesgos derivados de actos de corrupción.",
                      "Consultoría legal en relación con el cumplimiento con la normativa para la prevención del lavado de activos y financiación del terrorismo."
                    ].map((item, i) => (
                      <Typography component="li" key={i} sx={{ mb: 1.5, fontSize: '0.95rem' }}>{item}</Typography>
                    ))}
                  </Box>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
              <Button 
                onClick={handleCloseModalInnovacion} 
                autoFocus
                variant="outlined"
                sx={{
                  color: '#B8C5B8',
                  borderColor: 'rgba(184, 197, 184, 0.4)',
                  '&:hover': {
                    borderColor: '#FFFFFF',
                    color: '#FFFFFF',
                    backgroundColor: 'rgba(255,255,255,0.05)'
                  }
                }}
              >
                Cerrar
              </Button>
            </DialogActions>
          </Dialog>

          {/* Modal Derecho Laboral, Seguridad Social y Migratorio */}
          <Dialog
            open={openModalLaboral}
            onClose={handleCloseModalLaboral}
            aria-labelledby="alert-dialog-title-laboral"
            aria-describedby="alert-dialog-description-laboral"
            PaperProps={{
              style: {
                backgroundColor: 'rgba(36, 44, 39, 0.98)',
                backdropFilter: 'blur(12px)',
                border: '1px solid rgba(184, 197, 184, 0.2)',
                color: '#ffffff',
                borderRadius: '0px',
                maxWidth: '1200px', // Wider due to number of columns
                width: '100%',
              },
            }}
          >
            <DialogTitle id="alert-dialog-title-laboral" sx={{ pt: 4, pb: 2, px: { xs: 3, md: 5 } }}>
              <Typography variant="overline" sx={{ color: '#B8C5B8', fontSize: '0.75rem', letterSpacing: '0.1em' }}>
                Área de Práctica
              </Typography>
              <Typography variant="h4" sx={{ color: '#FFFFFF', textTransform: 'uppercase', letterSpacing: '0.02em', fontWeight: 600, fontSize: { xs: '1.5rem', md: '2rem' } }}>
                Derecho Laboral, Seguridad Social & Migratorio
              </Typography>
            </DialogTitle>
            
            <DialogContent sx={{ px: { xs: 3, md: 5 }, pb: 4 }}>
              <Box 
                sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, 
                  gap: { xs: 4, md: 4 },
                  mt: 2 
                }}
              >
                {/* Columna 1: Consultoría Laboral */}
                <Box>
                  <Box sx={{ borderBottom: '1px solid rgba(184, 197, 184, 0.3)', mb: 3, pb: 1 }}>
                     <Typography variant="h6" sx={{ color: '#B8C5B8', textTransform: 'uppercase', fontSize: '0.9rem', letterSpacing: '0.05em', fontWeight: 500 }}>
                      Consultoría Laboral
                    </Typography>
                  </Box>
                  <Box sx={{ color: '#D4E0D4', fontFamily: 'inherit', lineHeight: 1.8 }}>
                    <Typography paragraph sx={{ fontSize: '0.85rem' }}>
                      Brindar asesoraría en la gestión de las relaciones laborales individuales con especial énfasis en los requerimientos legales de contratación, esquemas de remuneración, régimen disciplinario y desvinculación.
                    </Typography>
                    <Typography paragraph sx={{ fontSize: '0.85rem' }}>
                      Diseño e implementación de estrategias para cumplimiento normativo laboral y mitigación de contingencias jurídicas.
                    </Typography>
                    <Typography sx={{ fontSize: '0.85rem' }}>
                      Procesos de Due Diligence en materia laboral sobre cumplimiento de normas y obligaciones laborales, de seguridad social y de derechos humanos.
                    </Typography>
                  </Box>
                </Box>

                 {/* Columna 2: Derecho Colectivo & Proyectos */}
                 <Box>
                  <Box sx={{ borderBottom: '1px solid rgba(184, 197, 184, 0.3)', mb: 3, pb: 1 }}>
                     <Typography variant="h6" sx={{ color: '#B8C5B8', textTransform: 'uppercase', fontSize: '0.9rem', letterSpacing: '0.05em', fontWeight: 500 }}>
                      Derecho Colectivo & Proyectos
                    </Typography>
                  </Box>
                  <Box sx={{ color: '#D4E0D4', fontFamily: 'inherit', lineHeight: 1.8 }}>
                    <Typography paragraph sx={{ fontSize: '0.85rem', fontWeight: 700, mb: 1, color: '#FFFFFF' }}>
                      Derecho Colectivo
                    </Typography>
                    <Typography paragraph sx={{ fontSize: '0.85rem' }}>
                      Acompañamiento integral en negociaciones sindicales, acuerdos colectivos y fortalecimiento de relaciones laborales sostenibles.
                    </Typography>
                    <Typography paragraph sx={{ fontSize: '0.85rem', fontWeight: 700, mb: 1, color: '#FFFFFF' }}>
                      Proyectos y Auditorías
                    </Typography>
                    <Typography sx={{ fontSize: '0.85rem' }}>
                      Ejecución de auditorías laborales, diagnósticos de cumplimiento, proyectos de People Analytics y Legal Tech.
                    </Typography>
                  </Box>
                </Box>

                {/* Columna 3: Litigios */}
                <Box>
                  <Box sx={{ borderBottom: '1px solid rgba(184, 197, 184, 0.3)', mb: 3, pb: 1 }}>
                    <Typography variant="h6" sx={{ color: '#B8C5B8', textTransform: 'uppercase', fontSize: '0.9rem', letterSpacing: '0.05em', fontWeight: 500 }}>
                      Litigios
                    </Typography>
                  </Box>
                  <Box sx={{ color: '#D4E0D4', fontFamily: 'inherit', lineHeight: 1.8 }}>
                    <Typography paragraph sx={{ fontSize: '0.85rem' }}>
                      Representación judicial y administrativa ante procesos laborales, contenciosos administrativos y acciones de tutela a nivel nacional.
                    </Typography>
                    <Typography paragraph sx={{ fontSize: '0.85rem' }}>
                      Representación ante autoridades administrativas (Ministerio de Trabajo, UGPP, Entidades de Seguridad Social) frente a requerimientos derivados de asuntos laborales o de seguridad social.
                    </Typography>
                  </Box>
                </Box>

                {/* Columna 4: Asesoría Migratoria */}
                <Box>
                  <Box sx={{ borderBottom: '1px solid rgba(184, 197, 184, 0.3)', mb: 3, pb: 1 }}>
                    <Typography variant="h6" sx={{ color: '#B8C5B8', textTransform: 'uppercase', fontSize: '0.9rem', letterSpacing: '0.05em', fontWeight: 500 }}>
                      Asesoría Migratoria
                    </Typography>
                  </Box>
                  <Box component="ul" sx={{ pl: 2, m: 0, color: '#D4E0D4', fontFamily: 'inherit', lineHeight: 1.8 }}>
                    {[
                      "Trámite de visas en Colombia (requisitos, cartas, formularios).",
                      "Trámites administrativos de profesiones reguladas, convalidaciones y matrículas.",
                      "Representación ante Migración Colombia (UAEMC):",
                      "Permisos de ingreso y permanencia.",
                      "Registro en subdirección de extranjería.",
                      "Obtención de cédula de extranjería.",
                      "Movimientos migratorios y salvoconductos."
                    ].map((item, i) => (
                      <Typography component="li" key={i} sx={{ mb: 1, fontSize: '0.85rem' }}>{item}</Typography>
                    ))}
                  </Box>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
              <Button 
                onClick={handleCloseModalLaboral} 
                autoFocus
                variant="outlined"
                sx={{
                  color: '#B8C5B8',
                  borderColor: 'rgba(184, 197, 184, 0.4)',
                  '&:hover': {
                    borderColor: '#FFFFFF',
                    color: '#FFFFFF',
                    backgroundColor: 'rgba(255,255,255,0.05)'
                  }
                }}
              >
                Cerrar
              </Button>
            </DialogActions>
          </Dialog>

        </Container>
      </Box>

      {/* Innovación */}
      <Box
        id="innovacion"
        sx={{
          py: { xs: 8, md: 10 },
          backgroundColor: "#1F2330",
          color: "#FFFFFF",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Imagen de fondo fragmentada izquierda con transición suave */}
        <Box
          sx={{
            position: "absolute",
            top: "-10%",
            left: "-5%",
            width: "50%",
            height: "120%",
            backgroundImage: "url(/bosque-fondo.jpg)",
            backgroundSize: "cover",
            backgroundPosition: "center left",
            backgroundRepeat: "no-repeat",
            opacity: 0.12,
            zIndex: 0,
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                "linear-gradient(90deg, transparent 0%, rgba(31, 35, 48, 0.7) 40%, rgba(31, 35, 48, 0.95) 60%, #1F2330 75%)",
            },
            "&::after": {
              content: '""',
              position: "absolute",
              top: "-20%",
              left: 0,
              right: 0,
              height: "20%",
              background:
                "linear-gradient(180deg, #1F2330 0%, transparent 100%)",
            },
          }}
        />
        {/* Línea divisoria con verde */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: "10%",
            right: "10%",
            height: "1px",
            background:
              "linear-gradient(90deg, transparent, rgba(184, 197, 184, 0.4), transparent)",
            zIndex: 1,
          }}
        />

        <Container
          maxWidth="lg"
          sx={{ position: "relative", zIndex: 2, px: { xs: 4, md: 6 } }}
        >
          <Box sx={{ maxWidth: "1200px", mx: "auto", mb: 4 }}>
            <ScrollInteraction animationType="slide" delay={0.2}>
              <Typography
                variant="overline"
                sx={{
                  fontSize: "0.6875rem",
                  fontWeight: 400,
                  letterSpacing: "0.08em",
                  color: "#B8C5B8",
                  mb: 2,
                  textTransform: "uppercase",
                  opacity: 0.9,
                  display: "block",
                }}
              >
                Tecnología Legal
              </Typography>
              <ScrollText
                variant="h2"
                animationType="word"
                stagger={0.1}
                sx={{
                  fontSize: { xs: "2rem", md: "3rem", lg: "3.5rem" },
                  fontWeight: 700,
                  mb: 3,
                  color: "#B8C5B8",
                  lineHeight: 1.1,
                  letterSpacing: "-0.01em",
                  textAlign: "left",
                }}
              >
                IA, automation, data y precisión: la nueva práctica del derecho.
              </ScrollText>
            </ScrollInteraction>

            <ScrollInteraction animationType="fade" delay={0.8}>
              <Typography
                variant="h4"
                sx={{
                  fontSize: { xs: "1.125rem", md: "1.375rem", lg: "1.5rem" },
                  fontWeight: 400,
                  color: "#D4E0D4",
                  mb: 4,
                  textAlign: "left",
                  maxWidth: "700px",
                }}
              >
                Nuestra IA no reemplaza abogados: los potencia. Les da más
                información, más capacidad de predicción y más tiempo para
                pensar estratégicamente.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: "1.1rem",
                  fontWeight: 400,
                  color: "#B8C5B8",
                  mb: 4,
                  textAlign: "left",
                  maxWidth: "700px",
                }}
              >
                Además, somos parte del Grupo LoBueno, un ecosistema
                latinoamericano de compañías innovadoras y tecnológicas con
                impacto global.
              </Typography>
            </ScrollInteraction>

            {/* Servicios de Tecnología Legal - Update from content.md */}
            <ScrollInteraction animationType="fade" delay={1.2}>
              <Box sx={{ mt: 4 }}>
                <InnovationTabs
                  items={[
                    {
                      title: "El Saber hacer",
                      description:
                        "Característica principal de nuestra firma: la experiencia, medida no en tiempo sino en calidad del trabajo. Responsabilidad, dedicación y profundidad en los problemas jurídicos que subyacen en los negocios.",
                    },
                    {
                      title: "Soluciones jurídicas modernas",
                      description:
                        "Un modelo legal moderno para empresas que necesitan decisiones claras, rápidas y basadas en datos. Combinamos experiencia jurídica, tecnología e inteligencia artificial para maximizar valor y reducir riesgos.",
                    },
                    {
                      title: "LegalTech que automatiza",
                      description:
                        "Automatizamos procesos jurídicos, optimizamos tiempos y generamos trazabilidad real. Herramientas de IA y dashboards inteligentes al servicio del negocio.",
                    },
                    {
                      title: "Expertos en derecho empresarial",
                      description:
                        "Acompañamos a organizaciones en sus retos corporativos, laborales, minero–energéticos y públicos. Soluciones legales diseñadas para impulsar crecimiento y estabilidad.",
                    },
                    {
                      title: "Decisiones con Analítica e IA",
                      description:
                        "Integración de analítica, Power BI y modelos predictivos que permiten anticipar riesgos. Transparencia, precisión y control en cada decisión jurídica.",
                    },
                  ]}
                />
              </Box>
            </ScrollInteraction>
          </Box>
        </Container>
      </Box>

      {/* Talento */}
      <Box
        id="talento"
        sx={{
          py: { xs: 8, md: 12 },
          backgroundColor: "#F2F4F2",
          position: "relative",
        }}
      >
        {/* Línea divisoria sutil */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: "10%",
            right: "10%",
            height: "1px",
            background:
              "linear-gradient(90deg, transparent, rgba(31, 35, 48, 0.1), transparent)",
          }}
        />
        <Container maxWidth="lg" sx={{ px: { xs: 4, md: 6 } }}>
          <Box sx={{ maxWidth: "1200px", mx: "auto", mb: 8 }}>
            <Typography
              variant="overline"
              sx={{
                fontSize: "0.6875rem",
                fontWeight: 400,
                letterSpacing: "0.08em",
                color: "#1F2330",
                mb: 2,
                textTransform: "uppercase",
                opacity: 0.6,
                display: "block",
              }}
            >
              Expertos
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "2rem", md: "3rem", lg: "3.5rem" },
                fontWeight: 700,
                mb: 8,
                color: "#1F2330",
                lineHeight: 1.1,
                letterSpacing: "-0.01em",
                textAlign: "left",
              }}
            >
              Humanos brillantes. Tecnología brillante.
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontSize: { xs: "1.5rem", md: "2rem" },
                fontWeight: 300,
                color: "#1F2330",
                mb: 4,
                textAlign: "center",
                maxWidth: "900px",
                mx: "auto",
                fontStyle: "italic",
                lineHeight: 1.4,
              }}
            >
              "En Sólido no contratamos abogados para adaptarse al futuro. Los
              formamos para construirlo."
            </Typography>
          </Box>


        </Container>
      </Box>

      {/* Clientes */}
      <ClientsSection />

      {/* Contacto */}
      <Box
        id="contacto"
        sx={{
          py: { xs: 8, md: 12 },
          backgroundColor: "#242C27",
          color: "#FFFFFF",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Imagen de fondo sutil */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            width: "45%",
            height: "100%",
            backgroundImage: "url(/bosque-fondo.jpg)",
            backgroundSize: "cover",
            backgroundPosition: "center right",
            backgroundRepeat: "no-repeat",
            opacity: 0.08,
            zIndex: 0,
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "linear-gradient(90deg, #242C27 0%, transparent 40%)",
            },
          }}
        />

        {/* Línea divisoria sutil */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: "10%",
            right: "10%",
            height: "1px",
            background:
              "linear-gradient(90deg, transparent, rgba(184, 197, 184, 0.4), transparent)",
            zIndex: 1,
          }}
        />

        <Container
          maxWidth="lg"
          sx={{ position: "relative", zIndex: 2, px: { xs: 4, md: 6 } }}
        >
            {/* Contenido Split: Contact Cards (Izq) y Form (Der) */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1.2fr" },
                gap: { xs: 6, md: 8 },
              }}
            >
              {/* Columna Izquierda: Mensaje y Cards Directas */}
              <Box>
                {/* Header */}
                <Box sx={{ mb: 6, textAlign: "left" }}>
                  <Typography
                    variant="overline"
                    sx={{
                      fontSize: "0.6875rem",
                      fontWeight: 400,
                      letterSpacing: "0.08em",
                      color: "#B8C5B8",
                      mb: 2,
                      textTransform: "uppercase",
                      opacity: 0.9,
                      display: "block",
                    }}
                  >
                    Contacto
                  </Typography>
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: "2rem", md: "2.5rem", lg: "3rem" },
                      fontWeight: 700,
                      mb: 3,
                      color: "#FFFFFF",
                      lineHeight: 1.1,
                      letterSpacing: "-0.01em",
                    }}
                  >
                    El futuro legal está ocurriendo ahora.
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: { xs: "0.9375rem", md: "1.0625rem" },
                      fontWeight: 400,
                      color: "#B8C5B8",
                      maxWidth: "700px",
                      lineHeight: 1.7,
                      mb: 4,
                    }}
                  >
                    ¿Listo para poner a Sólido del lado de su empresa?
                  </Typography>
                </Box>



              </Box>

              {/* Columna Derecha: Formulario */}
              <Box>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="h5"
                    sx={{ color: "#D4E0D4", mb: 1, fontWeight: 400 }}
                  >
                    Hablemos de estrategia.
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "#B8C5B8", opacity: 0.8 }}
                  >
                    Déjenos sus datos y le responderemos con una visión clara.
                  </Typography>
                </Box>
                <ContactForm />
              </Box>
            </Box>


        </Container>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          py: 8,
          backgroundColor: "#1F2330",
          borderTop: "1px solid rgba(184, 197, 184, 0.2)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Fondo de partículas sutil */}
        <ParticleBackground
          particleCount={20}
          speed={0.2}
          opacity={0.1}
          color="#B8C5B8"
        />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: "center",
              justifyContent: "space-between",
              gap: 4,
            }}
          >
            {/* Logo */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box
                component="img"
                src="/logo_solido_blanco.png"
                alt="Sólido Business Law"
                sx={{
                  height: 50,
                  filter: "brightness(1.2)",
                }}
              />
            </Box>

            {/* Texto central */}
            <Box sx={{ textAlign: "center", flex: 1 }}>
              <Typography
                variant="body1"
                sx={{
                  color: "#FFFFFF",
                  fontWeight: 500,
                  fontSize: "1rem",
                  letterSpacing: "0.02em",
                }}
              >
                LegalTech Specialist - Grupo Lo Bueno
              </Typography>
            </Box>

            {/* Iconos de contacto */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 3,
                justifyContent: { xs: "center", md: "flex-end" },
              }}
            >
              <IconButton
                href="mailto:contacto@solidobusinesslaw.com"
                sx={{
                  color: "#B8C5B8",
                  "&:hover": {
                    color: "#FFFFFF",
                    backgroundColor: "rgba(184, 197, 184, 0.1)",
                    transform: "scale(1.1)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                <Email />
              </IconButton>

              <IconButton
                href="tel:+573001234567"
                sx={{
                  color: "#B8C5B8",
                  "&:hover": {
                    color: "#FFFFFF",
                    backgroundColor: "rgba(184, 197, 184, 0.1)",
                    transform: "scale(1.1)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                <Phone />
              </IconButton>

              <IconButton
                href="https://www.google.com/maps/place/Ac+100+%2313-27,+Chapinero,+Bogot%C3%A1,+Cundinamarca/@4.6836863,-74.0489234,17z/data=!3m1!4b1!4m6!3m5!1s0x8e3f9a9660fd4c1b:0xb76fc61f33059113!8m2!3d4.683681!4d-74.0463485!16s%2Fg%2F11t5pdpzc_?entry=ttu&g_ep=EgoyMDI2MDEyMC4wIKXMDSoASAFQAw%3D%3D"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "#B8C5B8",
                  "&:hover": {
                    color: "#FFFFFF",
                    backgroundColor: "rgba(184, 197, 184, 0.1)",
                    transform: "scale(1.1)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                <LocationOn />
              </IconButton>
            </Box>
          </Box>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default App;
